<!-- Conversation list shared by the product's bubble (variant "popup") and the chat
     app's rail (variant "rail") — SPEC-2026-09-internal-chat-pwa, T-24. Search
     toolbar + shortcuts + sections; the host decides what opening means. -->
<div class="chat-list__toolbar">
  <ng-container [ngTemplateOutlet]="searchBar"></ng-container>

  @if (variant === 'rail') {
    <!-- Rail (DEV-1984): Slack-style vertical shortcuts —
         Threads · Saved · Unread filter · Team — above the sections. -->
    <nav class="chat-list__nav d-flex flex-column">
      <button
        type="button"
        class="chat-list__nav-link"
        [class.is-active]="activePane === 'threads'"
        [attr.aria-current]="activePane === 'threads' ? 'page' : null"
        (click)="openPane('threads')"
      >
        <i class="ri-discuss-line" aria-hidden="true"></i>
        {{ "chat.threadsView.title" | translate }}
      </button>
      <button
        type="button"
        class="chat-list__nav-link"
        [class.is-active]="activePane === 'saved'"
        [attr.aria-current]="activePane === 'saved' ? 'page' : null"
        (click)="openPane('saved')"
      >
        <i class="ri-bookmark-line" aria-hidden="true"></i>
        {{ "chat.saved.title" | translate }} ({{ savedCount }})
      </button>
      <!-- A filter, not a place: "on" reads in mint, never as the active pane. -->
      <button
        type="button"
        class="chat-list__nav-link"
        [class.is-on]="unreadOnly"
        [attr.aria-pressed]="unreadOnly"
        (click)="toggleUnreadOnly()"
      >
        <i
          [class]="unreadOnly ? 'ri-filter-3-fill' : 'ri-filter-3-line'"
          aria-hidden="true"
        ></i>
        {{ "chat.bubble.filterUnread" | translate }}
      </button>
      <button
        type="button"
        class="chat-list__nav-link"
        [class.is-active]="activePane === 'team'"
        [attr.aria-current]="activePane === 'team' ? 'page' : null"
        (click)="openPane('team')"
      >
        <i class="ri-team-line" aria-hidden="true"></i>
        {{ "chat.team.title" | translate }}
      </button>
    </nav>
  } @else {
    <div class="chat-list__quick d-flex align-items-center gap-3">
      <button
        type="button"
        class="chat-list__quick-link"
        (click)="openPane('saved')"
      >
        <i class="ri-bookmark-line" aria-hidden="true"></i>
        {{ "chat.saved.title" | translate }} ({{ savedCount }})
      </button>
      <button
        type="button"
        class="chat-list__quick-link"
        (click)="openPane('team')"
      >
        <i class="ri-team-line" aria-hidden="true"></i>
        {{ "chat.team.title" | translate }}
      </button>
      <button
        type="button"
        class="chat-list__quick-link ms-auto"
        [class.is-active]="unreadOnly"
        [attr.aria-pressed]="unreadOnly"
        (click)="toggleUnreadOnly()"
      >
        <i
          [class]="unreadOnly ? 'ri-filter-3-fill' : 'ri-filter-3-line'"
          aria-hidden="true"
        ></i>
        {{ "chat.bubble.filterUnread" | translate }}
      </button>
    </div>
  }
</div>

<div class="chat-list__scroll">
  @if (bootstrapPending$ | async) {
    <chat-sidebar-skeleton
      [rows]="5"
      [compact]="true"
    ></chat-sidebar-skeleton>
  } @else if (conversationItems.length === 0) {
    <chat-empty-state
      [compact]="true"
      icon="ri-chat-new-line"
      titleKey="chat.states.emptyConversations"
      hintKey="chat.states.emptyConversationsHint"
      actionKey="chat.states.startConversation"
      actionIcon="ri-chat-new-line"
      (action)="openNewDirect()"
    ></chat-empty-state>
  } @else if (noUnreadResults) {
    <chat-empty-state
      [compact]="true"
      icon="ri-check-double-line"
      titleKey="chat.bubble.emptyUnread"
      hintKey="chat.bubble.emptyUnreadHint"
    ></chat-empty-state>
  } @else if (noListResults) {
    <chat-empty-state
      [compact]="true"
      icon="ri-search-line"
      titleKey="chat.search.empty"
      hintKey="chat.search.emptyHint"
    ></chat-empty-state>
  } @else {
    @for (section of sections; track trackSection($index, section)) {
      <ng-container
        [ngTemplateOutlet]="conversationSection"
        [ngTemplateOutletContext]="{ $implicit: section }"
      ></ng-container>
    }

    @if (messageResults.length > 0 && !collapsed) {
      <p class="chat-list__section" id="chatListSectionMessages">
        <i class="ri-chat-quote-line" aria-hidden="true"></i>
        {{ "chat.search.tabMessages" | translate }}
      </p>
      <ng-container
        [ngTemplateOutlet]="messageHitList"
        [ngTemplateOutletContext]="{
          labelledBy: 'chatListSectionMessages',
        }"
      ></ng-container>
    }
  }
</div>

<ng-template #conversationSection let-section>
  <!-- Collapsed rail: no headers, and every section's rows show (nothing to expand them with). -->
  <div
    class="chat-list__section-head align-items-center"
    [class.d-flex]="!collapsed"
    [class.d-none]="collapsed"
  >
    <button
      type="button"
      class="chat-list__section chat-list__section--toggle flex-grow-1 overflow-hidden"
      [attr.aria-expanded]="isSectionExpanded(section.key)"
      [attr.aria-controls]="'chatListPanel-' + section.key"
      (click)="toggleSection(section.key)"
    >
      <i
        class="ri-arrow-right-s-line chat-list__section-caret"
        [class.is-open]="isSectionExpanded(section.key)"
        aria-hidden="true"
      ></i>
      <span class="text-truncate">
        {{
          section.translateTitle ? (section.title | translate) : section.title
        }}
      </span>
      <span class="chat-list__pill flex-shrink-0">{{ section.items.length }}</span>
    </button>

    @if (section.kind === "directs") {
      <button
        type="button"
        class="chat-list__section-action flex-shrink-0"
        (click)="openNewDirect()"
        [matTooltip]="'chat.sidebar.newConversation' | translate"
        [attr.aria-label]="'chat.sidebar.newConversation' | translate"
      >
        <i class="ri-chat-new-line" aria-hidden="true"></i>
      </button>
    } @else if (section.kind === "channels") {
      @if (canCreateChannel) {
        <button
          type="button"
          class="chat-list__section-action flex-shrink-0"
          (click)="openNewChannel()"
          [matTooltip]="'chat.channel.create' | translate"
          [attr.aria-label]="'chat.channel.create' | translate"
        >
          <i class="ri-add-line" aria-hidden="true"></i>
        </button>
      }
      <button
        type="button"
        class="chat-list__section-action flex-shrink-0"
        (click)="openBrowseChannels()"
        [matTooltip]="'chat.channel.explore' | translate"
        [attr.aria-label]="'chat.channel.explore' | translate"
      >
        <i class="ri-compass-3-line" aria-hidden="true"></i>
      </button>
    }
    @if (section.isDefaultSection) {
      <button
        type="button"
        class="chat-list__section-action flex-shrink-0"
        (click)="createSection()"
        [matTooltip]="'chat.sections.new' | translate"
        [attr.aria-label]="'chat.sections.new' | translate"
      >
        <i class="ri-folder-add-line" aria-hidden="true"></i>
      </button>
    } @else if (section.isCustomSection) {
      <span
        class="d-inline-flex flex-shrink-0"
        [matTooltip]="
          (section.canDelete
            ? 'chat.sections.delete'
            : 'chat.sections.deleteBlocked'
          ) | translate
        "
      >
        <button
          type="button"
          class="chat-list__section-action"
          [disabled]="!section.canDelete"
          (click)="deleteSection(section)"
          [attr.aria-label]="
            (section.canDelete
              ? 'chat.sections.delete'
              : 'chat.sections.deleteBlocked'
            ) | translate
          "
        >
          <i class="ri-delete-bin-line" aria-hidden="true"></i>
        </button>
      </span>
    }
  </div>

  @if (collapsed || isSectionExpanded(section.key)) {
    @if (section.items.length === 0) {
      <p class="chat-list__section-empty">
        {{ section.emptyKey | translate }}
      </p>
    } @else {
      <ul
        class="chat-list__list"
        [id]="'chatListPanel-' + section.key"
        [attr.aria-label]="
          section.translateTitle ? (section.title | translate) : section.title
        "
      >
        @for (conversation of section.items; track conversation.id) {
          <ng-container
            [ngTemplateOutlet]="conversationRow"
            [ngTemplateOutletContext]="{ $implicit: conversation }"
          ></ng-container>
        }
      </ul>
    }
  }
</ng-template>

<ng-template #searchBar>
  <div
    class="chat-list__search d-flex align-items-center gap-2 px-2 rounded mb-2"
  >
    <i class="ri-search-line flex-shrink-0" aria-hidden="true"></i>
    <input
      type="search"
      #searchBox
      class="chat-list__search-input flex-grow-1"
      [value]="searchTerm"
      (input)="search(searchBox.value)"
      [placeholder]="'chat.bubble.searchPlaceholder' | translate"
      [attr.aria-label]="'chat.bubble.searchPlaceholder' | translate"
    />
    @if (searchingMessages) {
      <span
        class="spinner-border spinner-border-sm flex-shrink-0"
        role="status"
        aria-hidden="true"
      ></span>
    } @else if (searchTerm) {
      <button
        type="button"
        class="chat-list__search-clear flex-shrink-0"
        (click)="clearSearch(); searchBox.focus()"
        [matTooltip]="'chat.search.clearFilters' | translate"
        [attr.aria-label]="'chat.search.clearFilters' | translate"
      >
        <i class="ri-close-line" aria-hidden="true"></i>
      </button>
    }
  </div>
</ng-template>
<ng-template #messageHitList let-labelledBy="labelledBy" let-label="label">
  <ul
    class="chat-list__list"
    [attr.aria-labelledby]="labelledBy"
    [attr.aria-label]="label"
  >
    @for (result of messageResults; track result.id) {
      <li class="chat-list__row">
        <button
          type="button"
          class="chat-list__row-main d-flex align-items-center gap-2"
          (click)="openMessageResult(result)"
        >
          <span class="chat-list__avatar flex-shrink-0">
            <i class="ri-chat-quote-line" aria-hidden="true"></i>
          </span>
          <span class="flex-grow-1 overflow-hidden text-start">
            <span class="chat-list__row-name d-block text-truncate">
              {{ result.authorName }} · {{ result.contextLabel }}
            </span>
            <span class="chat-list__row-preview d-block text-truncate">
              @for (
                segment of result.excerptSegments;
                track trackSegment($index, segment)
              ) {
                @if (segment.match) {
                  <mark class="chat-list__mark">{{ segment.text }}</mark>
                } @else {
                  <span>{{ segment.text }}</span>
                }
              }
            </span>
          </span>
          @if (result.participants.length > 0) {
            <span
              class="chat-list__participants flex-shrink-0"
              role="img"
              [attr.aria-label]="result.participantsLabel"
              [attr.title]="result.participantsLabel"
            >
              @for (participant of result.participants; track participant.id) {
                <span class="chat-list__participant">
                  @if (participant.image) {
                    <img
                      [src]="participant.image"
                      alt=""
                      (error)="onImageError($event)"
                    />
                  } @else {
                    {{ participant.initials }}
                  }
                </span>
              }
              @if (result.participantsOverflow > 0) {
                <span class="chat-list__participant">
                  +{{ result.participantsOverflow }}
                </span>
              }
            </span>
          }
        </button>
      </li>
    }
  </ul>
</ng-template>

<ng-template #conversationRow let-conversation>
  <li
    class="chat-list__row"
    [class.is-unread]="conversation.unread > 0"
    [class.is-muted]="conversation.muted"
    [class.is-active]="conversation.id === activeChannelId"
  >
    <button
      type="button"
      class="chat-list__row-main d-flex align-items-center gap-2"
      [attr.aria-current]="conversation.id === activeChannelId ? 'true' : null"
      [attr.aria-label]="collapsed ? conversation.name : null"
      [matTooltip]="collapsed ? conversation.name : ''"
      matTooltipPosition="right"
      (click)="openConversation(conversation)"
    >
      <span class="chat-list__avatar flex-shrink-0">
        @if (conversation.isDirect) {
          @if (conversation.image) {
            <img
              [src]="conversation.image"
              alt=""
              aria-hidden="true"
              (error)="onImageError($event)"
            />
          } @else {
            {{ conversation.initials }}
          }
          <span
            class="chat-avatar-tile__presence rounded-circle"
            [ngClass]="presenceDotClass(conversation)"
            aria-hidden="true"
          ></span>
        } @else {
          <i [ngClass]="conversation.icon" aria-hidden="true"></i>
        }
      </span>

      <span
        class="chat-list__row-name flex-grow-1 text-truncate text-start"
      >
        {{ conversation.name }}
      </span>

      @if (conversation.unread > 0) {
        <span
          class="chat-list__pill chat-list__pill--unread flex-shrink-0"
          [attr.aria-label]="'chat.bubble.filterUnread' | translate"
        >
          {{ conversation.unread > 99 ? "99+" : conversation.unread }}
        </span>
      }
    </button>

    <span class="dropdown chat-list__row-menu flex-shrink-0">
      <a
        href="javascript:void(0);"
        class="dropdown-toggle arrow-none card-drop"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        [attr.aria-label]="'chat.sidebar.rowMenu' | translate"
      >
        <i class="ri-more-2-fill"></i>
      </a>
      <div class="dropdown-menu dropdown-menu-end">
        @if (conversation.isMember) {
          <a
            href="javascript:void(0);"
            class="dropdown-item"
            (click)="markAsUnread(conversation)"
          >
            <i class="ri-mail-unread-line me-1"></i>
            {{ "chat.sidebar.markAsUnread" | translate }}
          </a>
        }
        <a
          href="javascript:void(0);"
          class="dropdown-item"
          (click)="toggleFavorite(conversation)"
        >
          <i
            class="me-1"
            [ngClass]="conversation.favored ? 'ri-star-fill' : 'ri-star-line'"
          ></i>
          {{
            (conversation.favored
              ? "chat.sidebar.removeFavorite"
              : "chat.sidebar.addFavorite"
            ) | translate
          }}
        </a>

        @if (conversation.favored) {
          <div class="dropdown-divider"></div>
          <h6 class="dropdown-header">
            {{ "chat.sections.moveTo" | translate }}
          </h6>
          @for (option of sectionOptions; track option.id) {
            <a
              href="javascript:void(0);"
              class="dropdown-item d-flex align-items-center justify-content-between"
              (click)="moveToSection(conversation, option.id)"
            >
              <span class="text-truncate">
                {{
                  option.translateTitle
                    ? (option.title | translate)
                    : option.title
                }}
              </span>
              @if (conversation.sectionId === option.id) {
                <i class="ri-check-line"></i>
              }
            </a>
          }
          <a
            href="javascript:void(0);"
            class="dropdown-item"
            (click)="createSection()"
          >
            <i class="ri-folder-add-line me-1"></i>
            {{ "chat.sections.new" | translate }}
          </a>
        }

        <!-- Membership-backed block: notification levels live on the
             membership row, so a non-member public channel has none. -->
        @if (conversation.isMember) {
          <div class="dropdown-divider"></div>
          <h6 class="dropdown-header">
            {{ "chat.sidebar.notifications" | translate }}
          </h6>
          @for (option of notificationLevels; track option.level) {
            <a
              href="javascript:void(0);"
              class="dropdown-item d-flex align-items-center justify-content-between"
              (click)="setNotificationLevel(conversation, option.level)"
            >
              <span>{{ option.labelKey | translate }}</span>
              @if (conversation.level === option.level) {
                <i class="ri-check-line"></i>
              }
            </a>
          }
        }

        <!-- Channel governance (spec §2: Admin archives/removes) — same gate
             as the pane header's delete: platform ADMIN, never DIRECT. -->
        @if (isPlatformAdmin && !conversation.isDirect) {
          <div class="dropdown-divider"></div>
          <a
            href="javascript:void(0);"
            class="dropdown-item text-danger"
            (click)="deleteConversation(conversation)"
          >
            <i class="ri-delete-bin-line me-1"></i>
            {{ "chat.channel.delete" | translate }}
          </a>
        }
      </div>
    </span>
  </li>
</ng-template>
